if (!customElements.get('order-bump')) {
  // Quantos produtos do carrinho (os mais recentes primeiro) servem de base para as recomendações.
  const BASE_PRODUCTS = 3;
  // Máximo aceito pela Product Recommendations API.
  const API_LIMIT = 10;

  // O cart drawer é re-renderizado a cada mudança no carrinho e recria o <order-bump>.
  // As respostas ficam em cache durante a página, e o último resultado é exibido na hora
  // enquanto o novo carrega, para o rodapé do drawer não encolher e crescer de novo.
  // A API já omite o que está no carrinho, então a chave do cache inclui os produtos dele.
  const responses = new Map();
  let lastSlides = [];

  class OrderBump extends HTMLElement {
    connectedCallback() {
      const productIds = this.dataset.productIds.split(',').filter(Boolean);
      const inCart = new Set(productIds);
      const limit = parseInt(this.dataset.limit, 10) || 6;

      this.render(lastSlides.filter((slide) => !inCart.has(slide.id)));
      this.load(productIds.slice(0, BASE_PRODUCTS), inCart, limit);
    }

    disconnectedCallback() {
      this.destroySwiper();
    }

    async load(baseIds, inCart, limit) {
      const intent = this.dataset.intent || 'complementary';
      const requestLimit = Math.min(limit + inCart.size, API_LIMIT);

      let slides = await this.collect(baseIds, intent, requestLimit, inCart, limit);
      if (!slides.length && intent !== 'related') {
        slides = await this.collect(baseIds, 'related', requestLimit, inCart, limit);
      }

      lastSlides = slides;
      if (this.isConnected) this.render(slides);
    }

    // Intercala as recomendações de cada produto base (1ª de cada, depois 2ª de cada...),
    // sem repetir produto e sem o que já está no carrinho.
    async collect(baseIds, intent, requestLimit, inCart, limit) {
      const lists = await Promise.all(baseIds.map((id) => this.fetchSlides(id, intent, requestLimit)));
      const longest = Math.max(0, ...lists.map((list) => list.length));
      const picked = new Map();

      for (let i = 0; i < longest && picked.size < limit; i++) {
        for (const list of lists) {
          const slide = list[i];
          if (!slide || inCart.has(slide.id) || picked.has(slide.id)) continue;
          picked.set(slide.id, slide);
          if (picked.size === limit) break;
        }
      }

      return [...picked.values()];
    }

    fetchSlides(productId, intent, requestLimit) {
      const key = `${intent}:${productId}:${requestLimit}:${this.dataset.productIds}`;
      if (responses.has(key)) return responses.get(key);

      const params = new URLSearchParams({
        product_id: productId,
        limit: requestLimit,
        intent,
        section_id: this.dataset.sectionId,
      });

      const request = fetch(`${this.dataset.url}?${params}`)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          return [...doc.querySelectorAll('[data-order-bump-product]')].map((element) => ({
            id: element.dataset.orderBumpProduct,
            html: element.outerHTML,
          }));
        })
        .catch((error) => {
          responses.delete(key);
          console.error('[order-bump]', error);
          return [];
        });

      responses.set(key, request);
      return request;
    }

    render(slides) {
      const ids = slides.map((slide) => slide.id).join(',');
      if (ids === this.renderedIds) return;
      this.renderedIds = ids;

      this.destroySwiper();
      this.querySelector('.swiper-wrapper').innerHTML = slides.map((slide) => slide.html).join('');
      this.hidden = slides.length === 0;
      if (slides.length) this.initSwiper();
    }

    initSwiper() {
      if (typeof Swiper === 'undefined') return;
      this.swiper = new Swiper(this.querySelector('.order-bump__swiper'), {
        slidesPerView: 1,
        spaceBetween: 5,
      });
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  customElements.define('order-bump', OrderBump);
}
